import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for cancellation form with reason selection
 *
 * This controller manages the form logic for selecting a cancellation reason.
 * It works with the existing modal controller for opening/closing.
 *
 * Usage:
 *   <form data-controller="cancellation-form">
 *     <select data-cancellation-form-target="reasonSelect"
 *             data-action="change->cancellation-form#onReasonChange">
 *       <option value="">Select a reason</option>
 *       <option value="error_entry">Error entry</option>
 *       <option value="other">Other</option>
 *     </select>
 *     <textarea data-cancellation-form-target="customReason"
 *               data-action="input->cancellation-form#onCustomReasonInput"></textarea>
 *     <input type="hidden" data-cancellation-form-target="reasonInput" name="cancellation_reason">
 *     <button data-cancellation-form-target="submitButton" disabled>Submit</button>
 *   </form>
 */
export default class extends Controller {
  static targets = ["reasonSelect", "customReason", "reasonInput", "submitButton"];

  connect() {
    // Initialize form state
    this.validateForm();
  }

  onReasonChange(event) {
    const selectedValue = event.target.value;
    const customReasonContainer = this.customReasonTarget?.closest("div")?.parentElement;

    if (selectedValue === "other") {
      // Show custom reason textarea
      if (this.hasCustomReasonTarget) {
        const container =
          document.getElementById(
            this.customReasonTarget.id.replace("custom-reason", "custom-reason-container")
          ) || this.customReasonTarget.closest("div");
        if (container) {
          container.classList.remove("hidden");
        }
        this.customReasonTarget.required = true;
        this.customReasonTarget.focus();
      }
    } else {
      // Hide custom reason textarea
      if (this.hasCustomReasonTarget) {
        const container =
          document.getElementById(
            this.customReasonTarget.id.replace("custom-reason", "custom-reason-container")
          ) || this.customReasonTarget.closest("div");
        if (container) {
          container.classList.add("hidden");
        }
        this.customReasonTarget.required = false;
        this.customReasonTarget.value = "";
      }
    }

    this.updateReasonInput();
    this.validateForm();
  }

  onCustomReasonInput() {
    this.updateReasonInput();
    this.validateForm();
  }

  updateReasonInput() {
    if (!this.hasReasonInputTarget) return;

    const selectedValue = this.hasReasonSelectTarget
      ? this.reasonSelectTarget.value
      : "";

    if (selectedValue === "other" && this.hasCustomReasonTarget) {
      this.reasonInputTarget.value = this.customReasonTarget.value;
    } else {
      // Get the display text of the selected option for the reason
      const selectedOption = this.reasonSelectTarget?.selectedOptions[0];
      this.reasonInputTarget.value = selectedOption?.text || selectedValue;
    }
  }

  validateForm() {
    if (!this.hasSubmitButtonTarget) return;

    const selectedValue = this.hasReasonSelectTarget
      ? this.reasonSelectTarget.value
      : "";

    let isValid = false;

    if (selectedValue && selectedValue !== "other") {
      isValid = true;
    } else if (
      selectedValue === "other" &&
      this.hasCustomReasonTarget &&
      this.customReasonTarget.value.trim().length > 0
    ) {
      isValid = true;
    }

    this.submitButtonTarget.disabled = !isValid;

    if (isValid) {
      this.submitButtonTarget.classList.remove(
        "opacity-50",
        "cursor-not-allowed"
      );
    } else {
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }
  }
}
